import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig, CustomNavigationClient, clearMsalCacheForce, clearMsalInteractionCache } from './src/authConfig';
import { seedDatabase } from './src/seedData';
import { ErrorBoundary } from './src/components/ErrorBoundary';

// Exponer la función reparada en la consola para inyección manual
(window as any).seedDatabase = seedDatabase;

// Detector de versión de frontend
const APP_VERSION = '2.0.2';
const initVersionCheck = async (): Promise<boolean> => {
  try {
    const oldVersion = localStorage.getItem('klydapp_app_version');
    if (oldVersion !== APP_VERSION) {
      console.log(`K-LyDapp version changed: ${oldVersion || 'none'} → ${APP_VERSION}`);
      
      if (window.caches) {
        try {
          const keys = await window.caches.keys();
          await Promise.all(keys.map(key => window.caches.delete(key)));
        } catch (e) {
          console.error('Error clearing Cache Storage during version update:', e);
        }
      }
      
      localStorage.setItem('klydapp_app_version', APP_VERSION);
      
      if (oldVersion !== null) {
        window.location.reload();
        return true;
      }
    }
  } catch (e) {
    console.error('Error in version detection:', e);
  }
  return false;
};

const versionCheckPromise = initVersionCheck();

import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { StatusBar, Style } from '@capacitor/status-bar';
import './src/styles/tokens.css';
import { modalStack } from './src/components/mobile/modal/modalStack';

// Exponer modalStack en window para inspección y control de botón atrás
if (typeof window !== 'undefined') {
    (window as any).modalStack = modalStack;
}

// --- Configuración Reactiva de Entorno Móvil K-LyDapp ---
if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);

    // 0. Simulación de Zonas Seguras en desarrollo (?safearea=1)
    const isSafeAreaSimulated = urlParams.get('safearea') === '1';
    if (isSafeAreaSimulated) {
        document.documentElement.classList.add('simulate-safe-area');
        document.documentElement.style.setProperty('--safe-top', '59px');
        document.documentElement.style.setProperty('--safe-bottom', '34px');
        document.documentElement.style.setProperty('--kl-safe-top', '59px');
        document.documentElement.style.setProperty('--kl-safe-bottom', '34px');
    }

    // 1. Detección reactiva de kl-mobile (<= 480px) con listener al girar o redimensionar
    const isDevNative = import.meta.env.DEV && (urlParams.get('native') === '1');
    const mobileMediaQuery = window.matchMedia('(max-width: 480px)');
    const updateMobileClass = () => {
        if (mobileMediaQuery.matches || Capacitor.isNativePlatform() || isDevNative) {
            document.documentElement.classList.add('kl-mobile');
        } else {
            document.documentElement.classList.remove('kl-mobile');
        }
    };
    updateMobileClass();
    mobileMediaQuery.addEventListener('change', updateMobileClass);

    // 2. Detección de plataforma nativa (o forzado en DEV con ?native=1)
    if (Capacitor.isNativePlatform() || isDevNative) {
        document.documentElement.classList.add('is-native');
    }

    // 2.0 Configuración de StatusBar en dispositivos nativos (texto oscuro sobre fondo blanco, overlay activo)
    if (Capacitor.isNativePlatform()) {
        StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
        StatusBar.setStyle({ style: Style.Light }).catch(() => {});
        StatusBar.setBackgroundColor({ color: '#ffffff' }).catch(() => {});
    }

    // 2.1 Detección de equipos lentos (kl-lowfx) en Android para desactivar desenfoque pesado
    if (typeof navigator !== 'undefined') {
        const isAndroid = /android/i.test(navigator.userAgent);
        const mem = (navigator as any).deviceMemory;
        const cores = navigator.hardwareConcurrency;
        if (isAndroid && ((mem !== undefined && mem <= 3) || (cores !== undefined && cores <= 4))) {
            document.documentElement.classList.add('kl-lowfx');
        }
    }

    // 3. Detección de teclado visible para ocultar botones flotantes y barra inferior (solo en móvil)
    const isMobileMode = () => document.documentElement.classList.contains('kl-mobile') || document.documentElement.classList.contains('is-native');

    if (window.visualViewport) {
        let vpResizeTimer: any = null;
        let lastVpHeight = window.visualViewport.height;

        window.visualViewport.addEventListener('resize', () => {
            if (!isMobileMode()) return;
            const currentH = window.visualViewport?.height || window.innerHeight;
            if (Math.abs(currentH - lastVpHeight) < 2) return;

            if (vpResizeTimer) clearTimeout(vpResizeTimer);
            vpResizeTimer = setTimeout(() => {
                requestAnimationFrame(() => {
                    const viewportHeight = window.visualViewport?.height || window.innerHeight;
                    if (Math.abs(viewportHeight - lastVpHeight) < 2) return;
                    lastVpHeight = viewportHeight;
                    const isKeyboardOpen = (window.innerHeight - viewportHeight) > 150;
                    document.body.classList.toggle('keyboard-open', isKeyboardOpen);
                });
            }, 100);
        });
    }

    // Fallback de teclado para elementos de formulario (solo en móvil)
    document.addEventListener('focusin', (e) => {
        if (!isMobileMode()) return;
        const target = e.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
            document.body.classList.add('keyboard-open');
        }
    });
    document.addEventListener('focusout', () => {
        document.body.classList.remove('keyboard-open');
    });

    // 4. Manejo del botón / gesto atrás de Android (Prioridad Fase 5):
    // 1) Panel emergente
    // 2) Ventana superior de la pila (modalStack)
    // 3) Menú lateral
    // 4) Navegación hacia atrás
    if (Capacitor.isNativePlatform()) {
        CapacitorApp.addListener('backButton', ({ canGoBack }) => {
            // 1. Si el panel de acciones rápidas está abierto, cerrarlo primero
            const quickActionsOpen = document.querySelector('[data-quick-actions-open="true"]');
            if (quickActionsOpen) {
                const closeBtn = quickActionsOpen.querySelector('[data-quick-actions-close="true"]') as HTMLElement | null;
                if (closeBtn) {
                    closeBtn.click();
                    return;
                }
            }

            // 2. Si hay ventanas abiertas en la pila modal (KlSheet, KlFullModal, KlDialog), cerrar la superior
            if (modalStack.closeTop()) {
                return;
            }

            // 3. Menú lateral si está abierto
            const openDrawer = document.querySelector('[data-mobile-menu-drawer="true"]');
            if (openDrawer) {
                const drawerCloseBtn = openDrawer.querySelector('button[data-drawer-close="true"]') as HTMLElement | null;
                if (drawerCloseBtn) {
                    drawerCloseBtn.click();
                    return;
                }
            }

            // B. Disparar evento para componentes que escuchen eventos de atrás personalizados
            const customBackEvent = new CustomEvent('kl-back-button', { cancelable: true });
            window.dispatchEvent(customBackEvent);
            if (customBackEvent.defaultPrevented) {
                return;
            }

            // C. Buscar solo diálogos VISIBLES (no ocultos)
            const candidateDialogs = Array.from(document.querySelectorAll(
                '[role="dialog"], .modal-backdrop, [data-modal="true"], .fixed.inset-0.z-50, .fixed.inset-0.z-\\[100\\]'
            )) as HTMLElement[];

            const visibleDialog = candidateDialogs.reverse().find(el => {
                const style = window.getComputedStyle(el);
                return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && (el.offsetWidth > 0 || el.offsetHeight > 0);
            });

            if (visibleDialog) {
                // Si el diálogo visible tiene un botón de cerrar explícito, lo clickeamos y retornamos
                const closeBtn = visibleDialog.querySelector(
                    '[data-kl-modal-close], .kl-dialog-close, button[aria-label="Cerrar"], button[aria-label="Close"], button.close, [data-close="true"]'
                ) as HTMLElement | null;

                if (closeBtn) {
                    closeBtn.click();
                    return;
                }
                // Si no hay botón "Cerrar" en el diálogo visible, NO hacemos return: dejamos que el comportamiento anterior continúe
            }

            // D. Comportamiento en pantalla principal (Panel): si no hay modales, volver a selección de obra
            const isAtDashboard = window.location.pathname === '/' || window.location.pathname === '';
            const hasSelectedProject = Boolean(localStorage.getItem('selectedProjectId'));
            if (isAtDashboard && hasSelectedProject) {
                window.dispatchEvent(new CustomEvent('kl-go-home'));
                return;
            }

            // E. Comportamiento por defecto
            if (canGoBack && window.history.length > 1) {
                window.history.back();
            } else {
                CapacitorApp.exitApp();
            }
        });
    }
}

const msalInstance = new PublicClientApplication(msalConfig);
msalInstance.setNavigationClient(new CustomNavigationClient());

// Promesa global de inicialización de MSAL
const msalInitPromise = msalInstance.initialize();

if (Capacitor.isNativePlatform()) {
    Browser.addListener('browserFinished', () => {
        console.log("[MSAL Mobile] Browser terminado/cerrado por el usuario (Global). Limpiando caché y UI.");
        clearMsalInteractionCache();
        window.dispatchEvent(new CustomEvent('msal-browser-closed'));
    });
}

// Función de sanitización de URL para logs de auditoría seguros
const sanitizeUrlForLog = (url: string) => {
    try {
        const urlObj = new URL(url.replace('klydapp://', 'http://localhost/'));
        const params = new URLSearchParams(urlObj.search || urlObj.hash.substring(1));
        if (params.has('code')) params.set('code', '***CODE_REDACTED***');
        if (params.has('session_state')) params.set('session_state', '***SESSION_STATE_REDACTED***');
        if (params.has('id_token')) params.set('id_token', '***TOKEN_REDACTED***');
        if (params.has('access_token')) params.set('access_token', '***TOKEN_REDACTED***');
        if (params.has('token')) params.set('token', '***TOKEN_REDACTED***');
        return url.split('?')[0].split('#')[0] + '?' + params.toString();
    } catch (e) {
        return url.substring(0, 40) + '... (error de parsing)';
    }
};

// clearMsalCacheForce se importa desde authConfig.ts

const handleDeepLink = async (rawUrl: string) => {
    if (!rawUrl) return;
    localStorage.setItem('isNative', 'true');
    console.log("[MSAL Mobile] Procesando enlace:", sanitizeUrlForLog(rawUrl));
    
    if (rawUrl.includes('k-lydapp.ldconstructora.cl') || rawUrl.startsWith('klydapp://')) {
        console.log("[MSAL Mobile] Cerrando navegador intermedio (In-App Browser)...");
        Browser.close().catch(console.error);
        
        // Esperar que la inicialización global de MSAL esté lista para evitar carreras
        console.log("[MSAL Mobile] Esperando inicialización de MSAL...");
        await msalInitPromise;
        console.log("[MSAL Mobile] MSAL Inicializado en deep link.");
        
        // 1. MSAL Handling
        let hashPart = "";
        if (rawUrl.includes("#")) {
            hashPart = "#" + rawUrl.split("#")[1];
        } else if (rawUrl.includes("?")) {
            hashPart = "#" + rawUrl.split("?")[1];
        }
        
        if (hashPart && (hashPart.includes("code=") || hashPart.includes("error="))) {
            console.log("[MSAL Mobile] Detectado hash/query de respuesta MSAL:", hashPart.split('&')[0] + "...");
            
            msalInstance.handleRedirectPromise({ hash: hashPart, navigateToLoginRequestUrl: false })
                .then((response) => {
                    if (response && response.account) {
                        console.log("[MSAL Mobile] Sesión MSAL completada con éxito para el usuario:", response.account.username);
                        msalInstance.setActiveAccount(response.account);
                    } else {
                        console.warn("[MSAL Mobile] handleRedirectPromise resolvió pero sin cuenta activa. Limpiando caché por seguridad.");
                        clearMsalCacheForce();
                    }
                })
                .catch((error) => {
                    console.error("[MSAL Mobile] Error procesando redirect de MSAL:", error);
                    clearMsalInteractionCache();
                    
                    const isCancel = error.message?.includes("access_denied") || 
                                     error.message?.includes("user_cancelled") || 
                                     error.errorCode?.includes("access_denied") ||
                                     error.errorCode?.includes("user_cancelled");
                                     
                    if (!isCancel) {
                        alert("Error de autenticación con Microsoft: " + (error.message || error) + "\n\nPor favor, intenta iniciar sesión nuevamente.");
                    } else {
                        console.log("[MSAL Mobile] El usuario canceló la autenticación.");
                    }
                    
                    window.dispatchEvent(new CustomEvent('msal-browser-closed'));
                });
        } else {
            console.log("[MSAL Mobile] No se detectó code ni error en el hash/query del deep link para MSAL.");
        }

        // 2. Supabase Magic Link & Universal Authentication Links Handling
        const isAuthUrl = 
            rawUrl.includes('/auth/invite') ||
            rawUrl.includes('/auth/otp') ||
            rawUrl.includes('/auth/callback') ||
            rawUrl.includes('/auth/confirm') ||
            rawUrl.includes('/auth-callback') ||
            rawUrl.startsWith('klydapp://auth');

        if (isAuthUrl) {
            try {
                let token = null;
                let type = 'magiclink';
                let email: string | null = null;
                
                if (rawUrl.startsWith('klydapp://')) {
                    // Extract query params manually since URL() might fail on custom schemes
                    const queryString = rawUrl.split('?')[1];
                    if (queryString) {
                        const params = new URLSearchParams(queryString);
                        token = params.get('token');
                        type = params.get('type') || 'magiclink';
                        email = params.get('email');
                    }
                } else {
                    const urlObj = new URL(rawUrl);
                    token = urlObj.searchParams.get('token');
                    email = urlObj.searchParams.get('email');
                    
                    // Deduce default authentication type from universal path prefix if not provided in query
                    const explicitType = urlObj.searchParams.get('type');
                    if (explicitType) {
                        type = explicitType;
                    } else {
                        const path = urlObj.pathname;
                        if (path.includes('/invite')) type = 'invite';
                        else if (path.includes('/otp')) type = 'magiclink';
                        else if (path.includes('/callback')) type = 'magiclink';
                        else if (path.includes('/confirm')) type = 'signup';
                        else type = 'magiclink';
                    }
                }
                
                if (token) {
                    const { supabase } = await import('./src/supabaseClient');
                    const accounts = msalInstance.getAllAccounts();
                    const currentMsal = accounts.length > 0 ? accounts[0].username : null;
                    const { data: { session } } = await supabase.auth.getSession();
                    const currentSupabase = session?.user?.email;
                    const activeCurrent = currentMsal || currentSupabase;

                    if (activeCurrent) {
                        if (email && activeCurrent.trim().toLowerCase() !== email.trim().toLowerCase()) {
                            const confirmSwitch = window.confirm(
                                `Esta invitación es para ${email}. Actualmente estás conectado como ${activeCurrent}.\n\n¿Deseas cerrar la sesión actual y continuar con la invitación?`
                            );
                            if (!confirmSwitch) {
                                console.log("[DeepLink] El usuario decidió no cambiar de cuenta.");
                                return;
                            }
                            await supabase.auth.signOut().catch(console.error);
                            clearMsalInteractionCache();
                            clearMsalCacheForce();
                        } else if (!email) {
                            // Enlace sin parámetro de correo (antiguo): cerrar sesión previa para aplicar la invitación limpiamente
                            await supabase.auth.signOut().catch(console.error);
                            clearMsalInteractionCache();
                            clearMsalCacheForce();
                        }
                    }

                    const { error } = await (supabase.auth as any).verifyOtp({ token_hash: token, type: type });
                    if (error) {
                        console.error("Error verifying OTP from deep link:", error);
                        alert("El enlace de acceso ha expirado o ya fue utilizado. Por favor, solicita uno nuevo.");
                    } else {
                        console.log("[DeepLink] Token de acceso verificado exitosamente.");
                        window.location.href = "/";
                    }
                }
            } catch (e) {
                console.error("Error parsing auth link:", e);
            }
        } else if (rawUrl.includes('#access_token=')) {
            try {
                const hashPart = rawUrl.split('#')[1];
                const params = new URLSearchParams(hashPart);
                const access_token = params.get('access_token');
                const refresh_token = params.get('refresh_token');
                
                if (access_token && refresh_token) {
                    const { supabase } = await import('./src/supabaseClient');
                    await supabase.auth.setSession({ access_token, refresh_token });
                }
            } catch (e) {
                console.error("Error setting Supabase session from deep link:", e);
            }
        }
    }
};

if (Capacitor.isNativePlatform()) {
    localStorage.setItem('isNative', 'true');

    // 1. Soporte para Cold Start (lanzamiento inicial de la app desde enlace externo)
    CapacitorApp.getLaunchUrl().then((launchUrl) => {
        if (launchUrl && launchUrl.url) {
            console.log("[DeepLink] Cold start URL detectada:", launchUrl.url);
            handleDeepLink(launchUrl.url);
        }
    }).catch(e => console.error("[DeepLink] Error al leer getLaunchUrl:", e));

    // 2. Listener para enlaces entrantes mientras la app está abierta o en segundo plano
    CapacitorApp.addListener('appUrlOpen', async (event: { url: string }) => {
        await handleDeepLink(event.url);
    });
}

msalInitPromise.then(() => {
    versionCheckPromise.then((didReload) => {
        if (didReload) {
            console.log("Version check triggered reload, aborting React mount.");
            return;
        }

        // En plataformas móviles nativas, handleRedirectPromise() no debe llamarse en el arranque general
        // porque al no haber hash en window.location (URL local del WebView) se limpia la caché temporal/PKCE
        // antes de que Capacitor dispare el evento 'appUrlOpen'.
        const handleRedirect = Capacitor.isNativePlatform() 
            ? Promise.resolve(null) 
            : msalInstance.handleRedirectPromise();

        handleRedirect.then((response) => {
            if (response && response.account) {
                msalInstance.setActiveAccount(response.account);
            } else {
                // Fallback: Check if there's already an active account in cache
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    msalInstance.setActiveAccount(accounts[0]);
                }
            }

            const rootElement = document.getElementById('root');
            if (!rootElement) {
              throw new Error("Could not find root element to mount to");
            }

            const root = ReactDOM.createRoot(rootElement);
            root.render(
              <React.StrictMode>
                <ErrorBoundary>
                  <MsalProvider instance={msalInstance}>
                    <App />
                  </MsalProvider>
                </ErrorBoundary>
              </React.StrictMode>
            );
        }).catch(error => {
            console.error("Error in MSAL redirect:", error);
            
            // Render anyway to show potential Unauthenticated state
            const rootElement = document.getElementById('root');
            if (rootElement) {
                const root = ReactDOM.createRoot(rootElement);
                root.render(
                  <React.StrictMode>
                    <ErrorBoundary>
                      <MsalProvider instance={msalInstance}>
                        <App />
                      </MsalProvider>
                    </ErrorBoundary>
                  </React.StrictMode>
                );
            }
        });
    });
});